import React, {useEffect, useReducer, useState} from 'react';
import {useParams} from 'react-router-dom';
import {isSafari} from 'react-device-detect';
import {MeshType, useGetMesh} from 'api/generated';
import {Button, Modal} from 'components';
import {NoImg} from 'assets';
import {ARButtonModelSelect} from './ar/core/buttons';

interface IIOS {
  style: React.CSSProperties;
  assignedMeshes: MeshType[];
  projectState: 'preview' | 'live';
}

const IOS = ({style, assignedMeshes, projectState}: IIOS) => {
  const [show, toggleShow] = useReducer(s => !s, false);
  const [selected, setSelected] = useState<MeshType>();
  const [started, setStarted] = useState(false);

  const params = useParams();
  const projectParam = params.project ?? '';

  const {refetch: getMesh} = useGetMesh(
    selected?.uid ?? '',
    {state: projectState, slug: projectParam, token: projectParam},
    {query: {enabled: false, staleTime: 60 * 1000}},
  );

  const handleOpen = () => {
    toggleShow();
  };

  const handleClose = () => {
    toggleShow();
    setSelected(undefined);
  };

  const handleStart = () => {
    setStarted(true);
    getMesh();
  };

  useEffect(() => {
    if (isSafari) {
      return;
    }
    // Chrome or other browsers may have issues that the Start AR link being not responsive after first try.
    // So reload the page to reset everything, when the page refocus after coming back from quick look.
    const controller = new AbortController();
    window.addEventListener('focus', () => location.reload(), {signal: controller.signal});
    return () => {
      controller.abort();
    };
  }, [isSafari]);

  useEffect(() => {
    if (!started) {
      return;
    }
    const timeout = setTimeout(() => {
      setStarted(false);
    }, 5000);
    return () => {
      clearTimeout(timeout);
    };
  }, [started]);

  return (
    <>
      <button type="button" style={style} className="cursor-pointer" onClick={handleOpen}>
        Explore Models
      </button>
      <Modal isVisible={show} handlePressOutside={handleClose}>
        <h2 className="text-xl font-medium">Select model</h2>
        <div className="max-h-80 grid grid-cols-2 gap-3 overflow-y-scroll no-scrollbar my-6">
          {assignedMeshes.map(mesh => {
            return (
              <ARButtonModelSelect
                key={mesh.uid}
                img={mesh.imageUrl || NoImg}
                label={mesh.name}
                onClick={() => setSelected(mesh)}
                selected={selected?.uid === mesh.uid}
              />
            );
          })}
        </div>
        <div className="flex flex-col gap-1.5">
          {selected && !started ? (
            // iOS quick look link
            <a
              key={selected.fileUsdzUrl}
              href={selected.fileUsdzUrl!}
              rel="ar"
              className="cursor-pointer flex justify-center items-center w-full rounded-md px-10 py-2 transition-all duration-200 text-white active:scale-95"
              style={{backgroundColor: style.backgroundColor ?? '#34bb78'}}
              onClick={handleStart}>
              Start AR
              {/* This img tag is necessary to bypass quick look preview and goes straight into AR */}
              <img src="" alt="quick_look_anchor" className="hidden" />
            </a>
          ) : (
            <Button text="Start AR" disabled loading={started} />
          )}
        </div>
      </Modal>
    </>
  );
};

export default IOS;
